import { useEventPlaces } from '@shared/utils/eventPlace';
import { useEventInfo } from '@shared/utils/eventInfo';

/**
 * To determine the event's location information,
 * including a display-friendly name for use in UI.
 *
 * @param locationName - Optional override for the event location name.
 * @returns An object containing:
 *   - `eventLocation`: The raw event location data.
 *   - `eventLocationDisplayName`: A human-readable location name for display.
 *   - `isLoadingEventLocation`: Boolean indicating if any of the data is still loading.
 */
export const useEventLocation = (locationName?: Readonly<Maybe<string>>) => {
  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const { venueInfo, loading: loadingVenueInfo } = useEventPlaces();

  const isLoadingEventLocation = loadingEventInfo || loadingVenueInfo;

  const eventLocation = eventInfo?.locationInfo;

  /**
   * If the experiment is enabled AND the event location is the same as the venue,
   * we prefer to show the city name instead to avoid redundant location display.
   * Default fall back to event location name
   */
  const isSamePlace = eventLocation?.placeId && eventLocation.placeId === venueInfo?.googlePlaceId;
  const eventLocationDisplayName = isSamePlace ? eventLocation?.city || eventLocation?.name : locationName || eventLocation?.name;

  return { eventLocation, eventLocationDisplayName, isLoadingEventLocation };
};
